import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { Search, User, LogOut, LayoutDashboard, Wallet, Share2, Sparkles } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { NotificationBell } from "./NotificationBell";

export const LOGO_URL = "https://customer-assets.emergentagent.com/job_097dcc50-4812-4b75-a884-fb87f03fa7a9/artifacts/6cfgy4bw_logo%20%281%29.webp";

const NAV = [
  { to: "/products", label: "Deals", id: "products" },
  { to: "/brands", label: "Brands", id: "brands" },
  { to: "/reels", label: "Reels", id: "reels" },
  { to: "/finance", label: "Finance", id: "finance" },
];

export const dashboardPath = (user) =>
  !user ? "/login" : user.user_type === "admin" ? "/admin" : user.user_type === "seller" ? "/seller/dashboard" : "/dashboard";

const SearchBox = ({ className = "", testId }) => {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        navigate(`/products${q.trim() ? `?q=${encodeURIComponent(q.trim())}` : ""}`);
      }}
      className={`relative ${className}`}
    >
      <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-primary-400" />
      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search deals, brands, products..."
        className="w-full h-11 pl-11 pr-4 rounded-full bg-white text-primary-900 placeholder:text-primary-300 text-sm outline-none ring-2 ring-transparent focus:ring-secondary-400 transition-shadow"
        data-testid={testId}
      />
    </form>
  );
};

const UserMenu = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  if (!user) {
    return (
      <div className="flex items-center gap-2">
        <Link to="/login" className="hidden sm:inline-flex px-4 h-10 items-center rounded-full text-white/90 hover:text-white hover:bg-white/10 font-semibold text-sm transition-colors" data-testid="login-btn">
          Login
        </Link>
        <Link to="/register" className="inline-flex px-4 sm:px-5 h-10 items-center rounded-full bg-secondary-500 hover:bg-secondary-400 text-primary-900 font-bold text-sm shadow-[0_4px_0_#B45309] active:translate-y-0.5 active:shadow-none transition-transform" data-testid="register-btn">
          <Sparkles className="w-4 h-4 mr-1.5" />Join Free
        </Link>
      </div>
    );
  }
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="flex items-center gap-2 h-10 pl-1 pr-3 rounded-full bg-white/15 hover:bg-white/25 text-white transition-colors" data-testid="user-menu-btn">
          <span className="w-8 h-8 rounded-full bg-secondary-500 text-primary-900 font-bold flex items-center justify-center">{user.name?.charAt(0)?.toUpperCase() || <User className="w-4 h-4" />}</span>
          <span className="hidden sm:block text-sm font-semibold max-w-[120px] truncate">{user.name}</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52 rounded-2xl">
        <DropdownMenuItem onClick={() => navigate(dashboardPath(user))} data-testid="menu-dashboard"><LayoutDashboard className="w-4 h-4 mr-2" />Dashboard</DropdownMenuItem>
        {user.user_type === "user" && (
          <>
            <DropdownMenuItem onClick={() => navigate("/wallet")} data-testid="menu-wallet"><Wallet className="w-4 h-4 mr-2" />Wallet</DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate("/referrals")} data-testid="menu-referrals"><Share2 className="w-4 h-4 mr-2" />Refer & Earn</DropdownMenuItem>
          </>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => { logout(); navigate("/"); }} className="text-red-600" data-testid="menu-logout"><LogOut className="w-4 h-4 mr-2" />Logout</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export const SiteHeader = () => (
  <header className="sticky top-0 z-40 bg-primary-700 shadow-lg shadow-primary-900/20" data-testid="site-header">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex items-center gap-4 h-16">
        <Link to="/" className="shrink-0 bg-white rounded-2xl px-3 py-1.5 -rotate-2 hover:rotate-0 transition-transform" data-testid="site-logo">
          <img src={LOGO_URL} alt="M Ventures" className="h-7 sm:h-8 w-auto" />
        </Link>
        <nav className="hidden lg:flex items-center gap-1 ml-2">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              className={({ isActive }) => `px-4 h-10 inline-flex items-center rounded-full text-sm font-semibold transition-colors ${isActive ? "bg-white text-primary-700" : "text-white/85 hover:text-white hover:bg-white/10"}`}
              data-testid={`nav-${n.id}`}
            >
              {n.label}
            </NavLink>
          ))}
        </nav>
        <SearchBox className="hidden md:block flex-1 max-w-md ml-auto" testId="header-search-input" />
        <div className="ml-auto md:ml-0 flex items-center gap-2"><NotificationBell /><UserMenu /></div>
      </div>
      <div className="md:hidden pb-3"><SearchBox testId="header-search-input-mobile" /></div>
    </div>
  </header>
);
